import { Component, ChangeDetectionStrategy } from '@angular/core';
import { FieldArrayType, FormlyModule } from '@ngx-formly/core';
import { NgIf, NgFor } from '@angular/common';
import { MatIconButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';

// Exported to reuse the same markup/styles for RepeatingAdditionalDebtType (Agreements)
export const REPEAT_SECTION_TEMPLATE = `
		<div
			class="repeat-section"
			[class.repeat-section-disabled]="props.disabled"
		>
			<!-- Header with label and add button -->
			<div class="repeat-section-header">
				<div class="section-header">
					<label class="section-title">{{ props.label }}</label>
				</div>

				<button
					*ngIf="!props.disabled"
					mat-icon-button
					color="primary"
					type="button"
					(click)="add()"
					[matTooltip]="props.addText || 'Add item'"
				>
					<mat-icon>add_circle_outline</mat-icon>
				</button>
			</div>

			<!-- List of items -->
			<div
				*ngFor="let field of field.fieldGroup; let i = index; let last = last"
				class="repeat-item"
				[class.repeat-item-last]="last"
				[class.repeat-item-deleted]="field.model?._delete"
			>
				<div class="repeat-item-content">
					<div class="repeat-item-index">{{ i + 1 }}</div>
					<div class="repeat-item-fields">
						<formly-field [field]="field"></formly-field>
					</div>
					<!-- Single toggle button for remove/restore -->
					<button
						*ngIf="!props.disabled"
						mat-icon-button
						[color]="field.model?._delete ? 'primary' : 'warn'"
						type="button"
						(click)="
								field.model?._delete ? restoreItem(field) : removeItem(field)
							"
						[matTooltip]="field.model?._delete ? 'Cancel remove' : 'Remove'"
						class="repeat-item-action"
					>
						<mat-icon>{{ field.model?._delete ? 'undo' : 'close' }}</mat-icon>
					</button>
				</div>
			</div>

			<!-- Empty state -->
			<div
				*ngIf="!field.fieldGroup || field.fieldGroup.length === 0"
				class="repeat-empty"
			>
				<mat-icon>inbox</mat-icon>
				<p>{{ props.emptyMessage || 'No items added yet' }}</p>
			</div>
		</div>
	`;

export const REPEAT_SECTION_STYLES = [
	`
			.repeat-section {
				border: 1px solid #e0e0e0;
				border-radius: 4px;
				background: #ffffff;
				margin-bottom: 24px;
			}

			.repeat-section-header {
				display: flex;
				align-items: center;
				justify-content: space-between;
				padding: 12px 16px;
				border-bottom: 1px solid #e0e0e0;
				background: #fafafa;
			}

			.repeat-section-label {
				font-size: 14px;
				font-weight: 500;
				color: #35418d;
			}

			.repeat-item {
				border-bottom: 1px solid #e0e0e0;
			}

			.repeat-item-last {
				border-bottom: none;
			}

			/* Deleted item styling */
			.repeat-item-deleted {
				background-color: #ffebee;
			}

			.repeat-item-deleted .repeat-item-fields {
				text-decoration: line-through;
				color: #757575;
			}

			.repeat-item-deleted .repeat-item-index {
				background-color: #ef5350;
			}

			.repeat-item-content {
				display: flex;
				align-items: flex-start;
				gap: 4px;
				padding: 24px 12px 0 12px;
				transition: background-color 0.2s;
			}

			.repeat-section:not(.repeat-section-disabled) .repeat-item-content:hover {
				background-color: #f5f5f5;
			}

			.repeat-section:not(.repeat-section-disabled)
				.repeat-item-deleted
				.repeat-item-content:hover {
				background-color: #ffe0e3;
			}

			.repeat-item-index {
				display: flex;
				align-items: center;
				justify-content: center;
				min-width: 28px;
				height: 28px;
				margin-top: 6px;
				border-radius: 50%;
				background-color: #35418d;
				color: white;
				font-size: 13px;
				font-weight: 500;
				flex-shrink: 0;
			}

			.repeat-item-fields {
				flex: 1;
				min-width: 0;
			}

			/* Reduce left/right margins for row and columns */
			.repeat-item-fields ::ng-deep .row {
				margin-left: 0;
				margin-right: 0;
			}

			.repeat-item-fields ::ng-deep .col,
			.repeat-item-fields ::ng-deep [class*='col-'] {
				padding-left: 4px;
				padding-right: 4px;
			}

			.repeat-item-action {
				flex-shrink: 0;
				width: 36px !important;
				height: 36px !important;
				margin-top: 2px;
				padding: 0 !important;
				display: flex !important;
				align-items: center !important;
				justify-content: center !important;
			}

			.repeat-item-action ::ng-deep .mat-mdc-button-persistent-ripple {
				border-radius: 50%;
			}

			.repeat-item-action ::ng-deep .mat-icon {
				font-size: 20px;
				width: 20px;
				height: 20px;
				line-height: 20px;
			}

			.repeat-item-action ::ng-deep .mat-mdc-button-touch-target {
				height: 36px;
				width: 36px;
			}

			.repeat-empty {
				display: flex;
				flex-direction: column;
				align-items: center;
				justify-content: center;
				padding: 32px 16px;
				color: #9e9e9e;
			}

			.repeat-empty mat-icon {
				font-size: 48px;
				width: 48px;
				height: 48px;
				margin-bottom: 8px;
				opacity: 0.5;
			}

			.repeat-empty p {
				font-size: 14px;
			}
		`,
];

@Component({
	selector: 'formly-repeat-section',
	template: REPEAT_SECTION_TEMPLATE,
	changeDetection: ChangeDetectionStrategy.Eager,
	styles: REPEAT_SECTION_STYLES,
	imports: [NgIf, MatIconButton, MatTooltip, MatIcon, NgFor, FormlyModule],
})
export class RepeatingSectionType extends FieldArrayType {
	/**
	 * Remove an item - mark as deleted if it has an id, otherwise remove from array
	 */
	removeItem(field: any): void {
		if (!this.field.fieldGroup) {
			return;
		}

		const index = this.field.fieldGroup.indexOf(field);
		if (index === -1) {
			return;
		}

		// If the item has an id (existing record), mark it as deleted
		// Otherwise, remove it from the array (new item that was never saved)
		if (field.model?.id) {
			field.model._delete = true;
			// Mark the form control as dirty so the parent form knows there are changes
			field.formControl?.markAsDirty();
		} else {
			// New item without id - actually remove it from the array
			this.remove(index);
		}
	}

	/**
	 * Restore a deleted item by removing the _delete flag
	 */
	restoreItem(field: any): void {
		if (field.model?._delete) {
			delete field.model._delete;
			// Mark the form control as dirty so the parent form knows there are changes
			field.formControl?.markAsDirty();
		}
	}
}
